import { Connection } from '@solana/web3.js';
import { Web3ModalScaffold } from '@web3modal/scaffold';
import { ApiController, AssetController, CoreHelperUtil, EventsController, NetworkController, OptionsController } from '@web3modal/core';
import { ConstantsUtil, HelpersUtil, PresetsUtil } from '@web3modal/scaffold-utils';
import { createWalletAdapters, syncInjectedWallets } from './connectors/walletAdapters.js';
import { SolConstantsUtil, SolHelpersUtil, SolStoreUtil } from './utils/scaffold/index.js';
import { WalletConnectConnector } from './connectors/walletConnectConnector.js';
export class Web3Modal extends Web3ModalScaffold {
    constructor(options) {
        const { solanaConfig, chains, tokens, _sdkVersion, chainImages, connectionSettings = 'confirmed', ...w3mOptions } = options;
        const { metadata } = solanaConfig;
        if (!solanaConfig) {
            throw new Error('web3modal:constructor - solanaConfig is undefined');
        }
        if (!w3mOptions.projectId) {
            throw new Error('web3modal:constructor - projectId is undefined');
        }
        const networkControllerClient = {
            switchCaipNetwork: async (caipNetwork) => {
                if (caipNetwork) {
                    try {
                        this.walletAdapters = createWalletAdapters(caipNetwork?.id.split(':')[1]);
                        const walletId = localStorage.getItem(SolConstantsUtil.WALLET_ID);
                        const wallet = walletId?.split('_')[1];
                        if (wallet === 'solflare' && window[wallet]) {
                            const adapter = this.walletAdapters[wallet];
                            await adapter.connect();
                            const address = adapter.publicKey?.toString();
                            this.setInjectedProvider(adapter, wallet, address);
                        }
                        await this.switchNetwork(caipNetwork);
                    }
                    catch (error) {
                        SolStoreUtil.setError(error);
                    }
                }
            },
            getApprovedCaipNetworksData: async () => new Promise(resolve => {
                const result = {
                    approvedCaipNetworkIds: undefined,
                    supportsAllNetworks: true
                };
                resolve(result);
            })
        };
        const connectionControllerClient = {
            connectWalletConnect: async (onUri) => {
                const WalletConnectProvider = await this.WalletConnectConnector.getProvider();
                if (!WalletConnectProvider) {
                    throw new Error('connectionControllerClient:getWalletConnectUri - provider is undefined');
                }
                WalletConnectProvider.on('display_uri', (uri) => {
                    onUri(uri);
                });
                const address = await this.WalletConnectConnector.connect();
                this.setWalletConnectProvider(address);
            },
            connectExternal: async ({ id }) => {
                const adapterId = this.transformWalletId(id);
                await this.walletAdapters[adapterId].connect();
                const address = this.walletAdapters[adapterId].publicKey?.toString();
                this.setInjectedProvider(this.walletAdapters[adapterId], adapterId, address);
            },
            disconnect: async () => {
                const provider = SolStoreUtil.state.provider;
                const providerType = SolStoreUtil.state.providerType;
                localStorage.removeItem(SolConstantsUtil.WALLET_ID);
                if (providerType === ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID) {
                    const WalletConnectProvider = provider;
                    await WalletConnectProvider.disconnect();
                }
                else if (provider) {
                    provider.emit('disconnect');
                }
                SolStoreUtil.reset();
            },
            signMessage: async (message) => {
                const provider = SolStoreUtil.state.provider;
                if (!provider) {
                    throw new Error('connectionControllerClient:signMessage - provider is undefined');
                }
                const signature = await provider.request({
                    method: 'personal_sign',
                    params: [message, this.getAddress()]
                });
                return signature;
            },
            estimateGas: async () => await Promise.resolve(BigInt(0)),
            getEnsAvatar: async (value) => await Promise.resolve(value),
            getEnsAddress: async (value) => await Promise.resolve(value),
            writeContract: async () => await Promise.resolve('0x'),
            sendTransaction: async () => await Promise.resolve('0x'),
            parseUnits: () => BigInt(0),
            formatUnits: () => ''
        };
        super({
            networkControllerClient,
            connectionControllerClient,
            defaultChain: SolHelpersUtil.getChainFromCaip(chains, typeof window === 'object' ? localStorage.getItem(SolConstantsUtil.CAIP_CHAIN_ID) : ''),
            tokens: HelpersUtil.getCaipTokens(tokens),
            _sdkVersion: _sdkVersion ?? `html-solana-${ConstantsUtil.VERSION}`,
            ...w3mOptions
        });
        this.hasSyncedConnectedAccount = false;
        this.chains = chains;
        this.connectionSettings = connectionSettings;
        this.syncRequestedNetworks(chains, chainImages);
        const chain = SolHelpersUtil.getChainFromCaip(chains, typeof window === 'object' ? localStorage.getItem(SolConstantsUtil.CAIP_CHAIN_ID) : '');
        if (chain) {
            SolStoreUtil.setCurrentChain(chain);
            SolStoreUtil.setCaipChainId(`solana:${chain.chainId}`);
        }
        this.syncNetwork(chainImages);
        this.walletAdapters = createWalletAdapters(chain?.chainId);
        this.WalletConnectConnector = new WalletConnectConnector({
            relayerRegion: 'wss://relay.walletconnect.com',
            metadata,
            chains,
            qrcode: true
        });
        SolStoreUtil.setConnection(new Connection(SolHelpersUtil.detectRpcUrl(chain, OptionsController.state.projectId), this.connectionSettings));
        SolStoreUtil.subscribeKey('address', () => {
            this.syncAccount();
        });
        SolStoreUtil.subscribeKey('caipChainId', () => {
            this.syncNetwork(chainImages);
        });
        AssetController.subscribeNetworkImages(() => {
            this.syncNetwork(chainImages);
        });
        NetworkController.subscribeKey('caipNetwork', () => {
            if (NetworkController.state.caipNetwork && !SolStoreUtil.state.isConnected) {
                SolStoreUtil.setCaipChainId(`solana:${chain.chainId}`);
                SolStoreUtil.setCurrentChain(chain);
                localStorage.setItem(SolConstantsUtil.CAIP_CHAIN_ID, `solana:${chain.chainId}`);
                ApiController.reFetchWallets();
            }
        });
        EventsController.subscribe(state => {
            if (state.data.event === 'SELECT_WALLET' && state.data.properties?.name === 'Phantom') {
                const isMobile = CoreHelperUtil.isMobile();
                const isClient = CoreHelperUtil.isClient();
                if (isMobile && isClient && !window.phantom) {
                    const href = window.location.href;
                    const protocol = href.startsWith('https') ? 'https' : 'http';
                    const host = href.split('/')[2];
                    const ref = `${protocol}://${host}`;
                    window.location.href = `https://phantom.app/ul/browse/${href}?ref=${ref}`;
                }
            }
        });
        if (CoreHelperUtil.isClient()) {
            this.checkActiveProviders();
            this.syncConnectors();
            let timer = 0;
            if (window.navigator.brave !== undefined &&
                window.navigator.brave.isBrave.name === 'isBrave') {
                timer = 100;
            }
            const checkWallet = () => {
                if (window.solflare) {
                    this.checkActiveProviders();
                    this.syncConnectors();
                }
                else {
                    setTimeout(() => {
                        checkWallet();
                    }, timer);
                }
            };
            setTimeout(() => {
                checkWallet();
            }, timer);
        }
    }
    setAddress(address) {
        SolStoreUtil.setAddress(address ?? '');
    }
    disconnect() {
        const provider = SolStoreUtil.state.provider;
        if (provider) {
            provider.emit('disconnect');
        }
    }
    getAddress() {
        const { address } = SolStoreUtil.state;
        return address ? SolStoreUtil.state.address : address;
    }
    getWalletProvider() {
        return SolStoreUtil.state.provider;
    }
    getWalletProviderType() {
        return SolStoreUtil.state.providerType;
    }
    getWalletConnection() {
        return SolStoreUtil.state.connection;
    }
    async checkActiveProviders() {
        const walletId = localStorage.getItem(SolConstantsUtil.WALLET_ID);
        try {
            if (walletId === ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID) {
                await this.WalletConnectConnector.connect(true);
                const provider = await this.WalletConnectConnector.getProvider();
                const accounts = await provider.enable();
                this.setWalletConnectProvider(accounts[0]);
            }
            else {
                const wallet = walletId?.split('_')[1];
                const adapter = this.walletAdapters[wallet];
                if (window[wallet]) {
                    await adapter.connect();
                    const address = adapter.publicKey?.toString();
                    this.setInjectedProvider(adapter, wallet, address);
                }
            }
        }
        catch (error) {
            SolStoreUtil.setError(error);
        }
    }
    transformWalletId(walletId) {
        return walletId.toLocaleLowerCase() === 'Trust'
            ? 'trustWallet'
            : walletId.toLocaleLowerCase();
    }
    syncConnectors() {
        const w3mConnectors = [];
        const connectorType = PresetsUtil.ConnectorTypesMap[ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID];
        if (connectorType) {
            w3mConnectors.push({
                id: ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID,
                explorerId: PresetsUtil.ConnectorExplorerIds[ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID],
                type: connectorType,
                imageUrl: 'https://avatars.githubusercontent.com/u/37784886',
                name: this.WalletConnectConnector.name,
                provider: this.WalletConnectConnector.getProvider()
            });
        }
        syncInjectedWallets(w3mConnectors, this.walletAdapters);
        this.setConnectors(w3mConnectors);
    }
    async syncAccount() {
        const address = SolStoreUtil.state.address;
        const chainId = SolStoreUtil.state.currentChain?.chainId;
        const isConnected = SolStoreUtil.state.isConnected;
        this.resetAccount();
        if (isConnected && address && chainId) {
            const caipAddress = `${ConstantsUtil.INJECTED_CONNECTOR_ID}:${chainId}:${address}`;
            this.setIsConnected(isConnected);
            this.setCaipAddress(caipAddress);
            await Promise.all([this.syncBalance(address)]);
            this.hasSyncedConnectedAccount = true;
        }
        else if (!isConnected && this.hasSyncedConnectedAccount) {
            this.resetWcConnection();
            this.resetNetwork();
        }
    }
    async syncBalance(address) {
        const caipChainId = SolStoreUtil.state.caipChainId;
        if (caipChainId && this.chains) {
            const chain = SolHelpersUtil.getChainFromCaip(this.chains, caipChainId);
            if (chain) {
                const balance = await this.WalletConnectConnector.getBalance(address);
                this.setBalance(balance.decimals.toString(), chain.currency);
            }
        }
    }
    syncRequestedNetworks(chains, chainImages) {
        const requestedCaipNetworks = chains?.map(chain => ({
            id: `solana:${chain.chainId}`,
            name: chain.name,
            imageId: PresetsUtil.EIP155NetworkImageIds[chain.chainId],
            imageUrl: chainImages?.[chain.chainId]
        }));
        this.setRequestedCaipNetworks(requestedCaipNetworks ?? []);
    }
    async switchNetwork(caipNetwork) {
        const caipChainId = caipNetwork.id;
        const providerType = SolStoreUtil.state.providerType;
        const chain = SolHelpersUtil.getChainFromCaip(this.chains, caipChainId);
        if (this.chains) {
            if (chain) {
                SolStoreUtil.setCaipChainId(`solana:${chain.chainId}`);
                SolStoreUtil.setCurrentChain(chain);
                localStorage.setItem(SolConstantsUtil.CAIP_CHAIN_ID, `solana:${chain.chainId}`);
                if (providerType?.includes(ConstantsUtil.INJECTED_CONNECTOR_ID)) {
                    const wallet = this.transformWalletId(providerType);
                    SolStoreUtil.setConnection(new Connection(SolHelpersUtil.detectRpcUrl(chain, OptionsController.state.projectId), this.connectionSettings));
                    this.setAddress(this.walletAdapters[wallet].publicKey?.toString());
                    await this.syncAccount();
                    return;
                }
                if (providerType === ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID) {
                    const universalProvider = await this.WalletConnectConnector.getProvider();
                    const namespaces = this.WalletConnectConnector.generateNamespaces(chain.chainId);
                    SolStoreUtil.setConnection(new Connection(SolHelpersUtil.detectRpcUrl(chain, OptionsController.state.projectId), this.connectionSettings));
                    universalProvider.connect({ namespaces, pairingTopic: undefined });
                    await this.syncAccount();
                }
            }
        }
    }
    async syncNetwork(chainImages) {
        const address = SolStoreUtil.state.address;
        const storeChainId = SolStoreUtil.state.caipChainId;
        const isConnected = SolStoreUtil.state.isConnected;
        if (this.chains) {
            const chain = SolHelpersUtil.getChainFromCaip(this.chains, storeChainId);
            if (chain) {
                const caipChainId = `solana:${chain.chainId}`;
                this.setCaipNetwork({
                    id: caipChainId,
                    name: chain.name,
                    imageId: PresetsUtil.EIP155NetworkImageIds[chain.chainId],
                    imageUrl: chainImages?.[chain.chainId]
                });
                if (isConnected && address) {
                    if (chain.explorerUrl) {
                        const url = `${chain.explorerUrl}/account/${address}`;
                        this.setAddressExplorerUrl(url);
                    }
                    else {
                        this.setAddressExplorerUrl(undefined);
                    }
                    if (this.hasSyncedConnectedAccount) {
                        await this.syncBalance(address);
                    }
                }
            }
        }
    }
    subscribeProvider(callback) {
        return SolStoreUtil.subscribe(callback);
    }
    async setWalletConnectProvider(address = '') {
        const caipChainId = `${SolStoreUtil.state.currentChain?.name}: ${SolStoreUtil.state.currentChain?.chainId}`;
        const chain = SolHelpersUtil.getChainFromCaip(this.chains, typeof window === 'object' ? localStorage.getItem(SolConstantsUtil.CAIP_CHAIN_ID) : '');
        if (chain) {
            SolStoreUtil.setCurrentChain(chain);
        }
        SolStoreUtil.setIsConnected(true);
        SolStoreUtil.setCaipChainId(caipChainId);
        SolStoreUtil.setProviderType('walletConnect');
        SolStoreUtil.setProvider(this.WalletConnectConnector);
        this.setAddress(address);
        window?.localStorage.setItem(SolConstantsUtil.WALLET_ID, ConstantsUtil.WALLET_CONNECT_CONNECTOR_ID);
        await Promise.all([this.syncBalance(address), this.getApprovedCaipNetworksData()]);
    }
    setInjectedProvider(provider, adapter, address = '') {
        window?.localStorage.setItem(SolConstantsUtil.WALLET_ID, `${ConstantsUtil.INJECTED_CONNECTOR_ID}_${adapter}`);
        const chainId = SolStoreUtil.state.currentChain?.chainId;
        const caipChainId = `solana:${chainId}`;
        if (address && chainId) {
            SolStoreUtil.setIsConnected(true);
            SolStoreUtil.setCaipChainId(caipChainId);
            SolStoreUtil.setProviderType(`injected_${adapter}`);
            SolStoreUtil.setProvider(provider);
            this.setAddress(address);
            this.watchInjected(provider);
            this.hasSyncedConnectedAccount = true;
        }
    }
    watchInjected(provider) {
        function disconnectHandler() {
            localStorage.removeItem(SolConstantsUtil.WALLET_ID);
            SolStoreUtil.reset();
            provider?.removeListener('disconnect', disconnectHandler);
            provider?.removeListener('accountsChanged', accountsChangedHandler);
            provider?.removeListener('connect', accountsChangedHandler);
        }
        function accountsChangedHandler(publicKey) {
            const currentAccount = publicKey.toBase58();
            if (currentAccount) {
                SolStoreUtil.setAddress(currentAccount);
            }
            else {
                localStorage.removeItem(SolConstantsUtil.WALLET_ID);
                SolStoreUtil.reset();
            }
        }
        if (provider) {
            provider.on('disconnect', disconnectHandler);
            provider.on('accountsChanged', accountsChangedHandler);
            provider.on('connect', accountsChangedHandler);
        }
    }
}
//# sourceMappingURL=client.js.map